import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { imgUrl } from "@/lib/api";

const TITLES = {
  cakes: "Signature Cakes",
  flowers: "Fresh Flowers",
  hampers: "Gift Hampers",
  mugs: "Coffee Mugs",
  combos: "Gift Combos",
};

const TAGLINES = {
  cakes: "Baked fresh, hand-frosted, always personalised.",
  flowers: "Hand-tied blooms, sourced the same morning.",
  hampers: "Curated boxes for every kind of celebration.",
  mugs: "Ceramic classics for the coffee-obsessed.",
  combos: "Cake + flowers + mugs — the double-take gift.",
};

export default function Shop() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setLoading(true);
    const url = category ? `/products?category=${category}` : "/products";
    api.get(url).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category]);

  const sorted = [...products].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    return 0;
  });

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">{category ? "Collection" : "Everything"}</div>
          <h1 className="font-display text-4xl md:text-6xl mt-2">{TITLES[category] || "All Gifts"}</h1>
          <p className="mt-3 text-[#6C665F] max-w-lg">{TAGLINES[category] || "Discover our full range of thoughtfully crafted gifts."}</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="overline">Sort</label>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="input-eko !py-2 !px-3 w-auto">
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 mt-8 overflow-x-auto no-scrollbar" data-testid="category-pills">
        {["all", "cakes", "flowers", "hampers", "mugs", "combos"].map((c) => (
          <Link
            key={c}
            to={c === "all" ? "/shop" : `/shop/${c}`}
            className={`px-5 py-2 rounded-full text-sm border whitespace-nowrap ${
              (category || "all") === c
                ? "bg-[#2D2A26] text-white border-[#2D2A26]"
                : "bg-white text-[#2D2A26] border-[#E5DFD3] hover:border-[#2D2A26]"
            }`}
            data-testid={`pill-${c}`}
          >
            {c === "all" ? "All" : TITLES[c]}
          </Link>
        ))}
      </div>

      {loading ? (
        <div className="mt-16 text-center text-[#6C665F]">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="mt-16 text-center text-[#6C665F]">No products yet in this collection.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 mt-12" data-testid="shop-grid">
          {sorted.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 8) * 0.04 }}>
              <Link to={`/product/${p.id}`} className="group block" data-testid={`shop-product-${p.id}`}>
                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4F2EC]">
                  <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover product-img" />
                </div>
                <div className="mt-4">
                  <div className="overline">{p.category}</div>
                  <div className="flex justify-between items-baseline mt-1">
                    <div className="font-display text-lg">{p.name}</div>
                    <div className="font-semibold">₹{p.price}</div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
